import chroma from 'chroma-js'

import { isIframeMode } from './iframeMode'
import { resolveNineNative, type NineNativeAPI } from './nineNative'
import { createSingleUseFunc } from './singleUse'

// ─────────────────────────────────────────────────────────────────────────────
// Type declarations & global augmentation
// ─────────────────────────────────────────────────────────────────────────────

type NativeReturnCallback = (result: unknown) => void

interface WebKitMessageHandler {
  postMessage: (message: unknown) => void
}

interface WebKitBridge {
  messageHandlers?: {
    nineHandler?: WebKitMessageHandler
    sizeNotification?: WebKitMessageHandler
  }
}

interface ParentLikeWindow extends Window {
  initNative?: () => string | undefined
}

interface ZoomInfo {
  originWidth: number
  originHeight: number
  ratio?: number
}

interface BodyOriginInfo {
  elem: HTMLElement
  width: string
  widthPriority: string
  minWidth: string
  minWidthPriority: string
}

interface MailViewOptions {
  uiBtn?: boolean
  mode?: 'normal' | 'optimized' | 'auto'
}

interface CSSPropInfo {
  type: 0 | 1
  value: string
  number?: number
  unit?: string
  realUnit?: string
  isRel?: boolean
  num?: number
}

interface RuleColorInfo {
  value: string
  isImportant: boolean
}

interface CssRuleInfo {
  selector?: string
  type: 1 | 4
  color?: RuleColorInfo
  backCol?: RuleColorInfo
  queryCondition?: string
  ruleInfoList?: CssRuleInfo[]
}

interface RuleListInfo {
  ruleInfoList: CssRuleInfo[]
}

interface StyleTagInfo {
  originTag: HTMLStyleElement
  ruleInfoList: CssRuleInfo[]
  darkModeTag: HTMLStyleElement
}

interface DarkModeInfo {
  foreColor: string
  backColor: string
  foreLum?: number
  backLum?: number
}

interface ResizeObserverEntry {
  prevWidth: number
  prevHeight: number
  element: HTMLElement | null
  observer?: ResizeObserver
  resizeTimer?: ReturnType<typeof setTimeout> | null
  calcHeight?: () => void
  start?: (isFirstCall?: boolean) => void
  clear?: () => void
}

// 확대(zoom) 버튼 — 미클릭 상태로 dispose/removeWrapper될 때 click 리스너를 해제할 수 있도록
// 핸들러를 노드에 보관한다. (R20)
type ExpandBtnElement = HTMLElement & {
  __nineExpandHandler?: (e: MouseEvent) => void
}

interface ViewerWebObjLike {
  setOptimizedBtn?: (key: string, value: boolean) => void
}

// Augmented window shape used internally; we cast `window` to it where
// these properties are accessed (avoids merging conflicts with other modules
// that augment Window with concrete `NineNative` / `MailView` shapes).
interface MailViewWindow extends Window {
  docUtil?: DocUtil
  MailView?: typeof MailView
  NineNative?: NineNativeAPI
  // `singleUse` 는 helper/singleUse.ts 가 lazy 초기화하므로 여기서 augment 하지 않음.
  _bodyBgColor?: string | null
  _tmpDarkMode?: { backColor: string; foreColor: string }
  NORMALIZE_MESSAGE_WIDTHS?: boolean
  // dev 전용(OFM-233 Phase 8 dev-emlview 하네스): true 이면 최적화(화면폭 맞춤 이미지 축소)를 끄고
  // 원본 크기로 렌더한다. raw 브라우저 렌더와 이미지 크기를 맞춰 픽셀 비교를 유의미하게 하기 위함.
  // /dev/mailview 페이지만 설정하며 프로덕션에는 설정되지 않는다.
  __devDisableOptimize?: boolean
  webkit?: WebKitBridge
  webObj?: ViewerWebObjLike
  setDarkMode?: (backColor: string, foreColor: string) => void
  isOfficeMailApp?: () => boolean
  nineLog?: (msg: string) => void
  img_find?: () => string[]
  reloadInlineImage?: (cid: string, uri: string) => void
}

const win = window as unknown as MailViewWindow

declare global {
  interface Window {
    docUtil?: DocUtil
    isReactReady?: boolean
    isMailViewReady?: boolean
  }

  interface HTMLElement {
    mvObj?: MailView
    initVisibility?: string
    imgListener?: (e: Event) => void
    imgListenerAS?: (e: Event) => void
    zoomInfo?: ZoomInfo
  }

  interface HTMLImageElement {
    initVisibility?: string
    imgListener?: (e: Event) => void
    imgListenerAS?: (e: Event) => void
  }
}

// iframe 컨텍스트 판별 — `isIframeMode()` 의 두 컨텍스트 (iframe / inner) 의미는
// helper/iframeMode.ts 의 doc 주석 참조. 모듈 평가 시 1 회 캡처 (런타임 중 불변).
const isIframe = isIframeMode()

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

// Static-template HTML assignment helper (no user input — XSS-safe).
function setHTML(el: Element, html: string): void {
  Reflect.set(el, 'innerHTML', html)
}

function getCSSProp(str: string, isAttr?: boolean): CSSPropInfo {
  const matchList = str?.match(/^(-{0,1}\d+[.]*\d*|\d*[.]*\d+)/)
  if (matchList != null && matchList.length > 0) {
    const numStr = matchList[0]
    let unit = str.substring(numStr.length)
    let num = numStr.indexOf('.') > -1 ? parseFloat(numStr) : parseInt(numStr, 10)
    switch (unit) {
      case 'pt':
        num = (num * 4) / 3
        unit = 'px'
        break
      case 'in':
        num = num * 96
        unit = 'px'
        break
      case 'pc':
        num = num * 16
        unit = 'px'
        break
      case 'cm':
        num = num * 37.79
        unit = 'px'
        break
      case 'mm':
        num = num * 3.779
        unit = 'px'
        break
    }
    if (isAttr === true && unit === '') {
      unit = 'px'
    }
    return {
      type: 1,
      number: num,
      num,
      unit: unit.length === 0 ? 'px' : unit,
      realUnit: unit,
      isRel: unit !== 'px',
      value: str,
    }
  }
  return { type: 0, value: str }
}

function printLog(...args: unknown[]): void {
  console.log(...args)
}

function createTag(tagText: string): HTMLElement {
  const divElem = document.createElement('div')
  setHTML(divElem, tagText)
  return divElem.firstElementChild as HTMLElement
}

function callWebView(param: string): void {
  printLog('call webview - ' + param)
  if (win.isOfficeMailApp?.() === true) {
    location.href = param
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// :scope selector polyfill (applied once at module load)
// ─────────────────────────────────────────────────────────────────────────────

;(function (doc: Document, proto: Element) {
  try {
    doc.querySelector(':scope body')
  } catch {
    ;(['querySelector', 'querySelectorAll'] as const).forEach((method) => {
      const protoRecord = proto as unknown as Record<string, Function>
      const nativ = protoRecord[method]
      protoRecord[method] = function (this: Element, selectors: string) {
        if (/(^|,)\s*:scope/.test(selectors)) {
          const id = this.id
          this.id = 'ID_' + new Date().getTime()
          const replaced = selectors.replace(/((^|,)\s*):scope/g, '$1#' + this.id)
          const result = (doc as unknown as Record<string, Function>)[method].call(doc, replaced)
          this.id = id
          return result
        }
        return nativ.call(this, selectors)
      }
    })
  }
})(window.document, Element.prototype as unknown as Element)

// ─────────────────────────────────────────────────────────────────────────────
// EditorMgr — color management, native bridge, dark-mode color computations
// ─────────────────────────────────────────────────────────────────────────────

export class EditorMgr {
  darkModeInfo: DarkModeInfo = {
    foreColor: '#ffffff',
    backColor: '#000000',
  }

  private _linkColor = {
    link: 'rgb(0, 0, 238)',
    visited: 'rgb(67, 0, 120)',
  }

  private _foreColorMap: Record<string, string> = {}
  private _backColorMap: Record<string, string> = {}
  private _foreColorRevMap: Record<string, string> = {}
  private _backColorRevMap: Record<string, string> = {}

  private _styleTagList: StyleTagInfo[] = []
  private _createInvitationTimer: ReturnType<typeof setTimeout> | null = null

  constructor(private docUtil: DocUtil) {}

  private get doc(): Document {
    return this.docUtil.doc
  }

  createSingleUseFunc(cb: NativeReturnCallback): string {
    // helper/singleUse.ts 가 owner — iframe / non-iframe 양쪽 컨텍스트를 일관되게 처리.
    return createSingleUseFunc(cb as (param: unknown) => void)
  }

  isPromise(value: unknown): value is Promise<unknown> {
    return value != null && typeof (value as { then?: unknown }).then === 'function'
  }

  nativeCall(
    funcName: string,
    returnCallback: NativeReturnCallback | null,
    paramArray?: unknown[],
  ): boolean {
    const nine = resolveNineNative()
    if (nine != null) {
      try {
        const fn = nine[funcName]
        if (fn != null) {
          const ret = fn.apply(nine, paramArray ?? [])
          if (returnCallback != null) {
            if (this.isPromise(ret)) {
              ret.then((result) => returnCallback(result))
            } else {
              returnCallback(ret)
            }
          }
          return true
        }
      } catch (e) {
        console.log(e)
      }
    } else if (win.webkit?.messageHandlers?.nineHandler?.postMessage) {
      let returnFuncName = ''
      if (returnCallback != null) {
        returnFuncName = this.createSingleUseFunc(returnCallback)
      }
      const param: { function: string; returnFunction: string; params?: unknown[] } = {
        function: funcName,
        returnFunction: returnFuncName,
      }
      if (paramArray != null && paramArray.length > 0) {
        param.params = paramArray
      }
      win.webkit.messageHandlers.nineHandler.postMessage(JSON.stringify(param))
      return true
    }
    return false
  }

  notifyCreateInvitation(startDateStr: string, endDateStr: string, displayStr: string): void {
    if (this._createInvitationTimer != null) {
      clearTimeout(this._createInvitationTimer)
    }
    this._createInvitationTimer = setTimeout(() => {
      console.log(' - notifyCreateInvitation - ', startDateStr, endDateStr, displayStr)
      this.nativeCall('createInvitation', null, [startDateStr, endDateStr, displayStr])
      this._createInvitationTimer = null
    }, 300)
  }

  _getHexColor(colStr: string): string {
    let crm: chroma.Color
    try {
      crm = chroma(colStr)
    } catch {
      if (colStr.toLowerCase() === 'transparent') return '#ffffff00'
      return '#808080'
    }
    return crm.hex()
  }

  _getDarkModeColor(_elem: HTMLElement | null, colStr: string, isBack: boolean): string {
    let crm: chroma.Color
    let alpha = 1
    try {
      crm = chroma(colStr)
      alpha = crm.alpha()
    } catch {
      if (colStr.toLowerCase() === 'transparent') return '#ffffff00'
      if (isBack === true) return '#000000'
      return '#ffffff'
    }

    let col = crm
    const lum = crm.luminance()
    let newLum: number

    if (lum === 0) {
      col = chroma(this.darkModeInfo.foreColor)
      if (col.alpha() === 0) col = col.alpha(1)
    } else if (lum === 1) {
      col = chroma(this.darkModeInfo.backColor)
      if (col.alpha() === 0) col = col.alpha(1)
      if (isBack === true) return col.hex()
    } else if (isBack === true) {
      if (lum < 0.01) {
        col = crm
      } else if (lum < 0.1) {
        col = crm.luminance(lum + 0.1)
      } else if (lum < 0.2) {
        col = crm.luminance(lum + 0.2)
      } else if (lum > 0.75) {
        newLum = 1 - (this.darkModeInfo.backLum ?? 0) - lum
        newLum = Math.max(newLum, 0)
        col = crm.luminance(newLum)
      }
    } else {
      if (lum < 0.2) {
        col = crm.luminance(0.5 - lum)
      } else if (lum > 0.9) {
        col = crm.luminance(1.0 - lum)
      } else if (lum > 0.75) {
        newLum = 1 - (this.darkModeInfo.backLum ?? 0) - lum
        newLum = Math.max(newLum, 0)
        col = crm.luminance(newLum)
      }
    }

    col = col.alpha(alpha)
    return col.hex()
  }

  _checkElemColor(elem: HTMLElement | null, targetCol?: string): void {
    if (this._foreColorMap['#000000'] == null) {
      const darkCol = this._getDarkModeColor(null, '#000000', false)
      this._foreColorMap['#000000'] = darkCol
      this._foreColorRevMap[darkCol] = 'rgb(0, 0, 0)'
      this._backColorMap['#000000'] = darkCol
      this._backColorRevMap[darkCol] = 'rgb(0, 0, 0)'
    }

    if (elem != null) {
      const foreColor = elem.style.color
      const backColor = elem.style.backgroundColor
      let newCol: string | null = null

      if (
        foreColor != null &&
        foreColor.length > 0 &&
        (targetCol === this._getHexColor(foreColor) || elem.getAttribute('data-nine-sc') == null)
      ) {
        newCol = this._getDarkModeColor(elem, foreColor, false)
        if (newCol !== foreColor) {
          elem.style.color = newCol
          this._foreColorMap[foreColor] = elem.style.color
          this._foreColorRevMap[newCol] = foreColor
          elem.setAttribute('data-nine-sc', foreColor)
        }
      }
      if (
        backColor != null &&
        backColor.length > 0 &&
        (targetCol === this._getHexColor(backColor) || elem.getAttribute('data-nine-sb') == null)
      ) {
        newCol = this._getDarkModeColor(elem, backColor, true)
        if (newCol != null && newCol !== backColor) {
          elem.style.backgroundColor = newCol
          this._backColorMap[backColor] = elem.style.backgroundColor
          this._backColorRevMap[newCol] = backColor
          elem.setAttribute('data-nine-sb', backColor)
        }
      }
      if (elem.tagName === 'FONT') {
        const fontColor = elem.getAttribute('color')
        if (
          fontColor != null &&
          fontColor.length > 0 &&
          (targetCol === this._getHexColor(fontColor) || elem.getAttribute('data-nine-ac') == null)
        ) {
          newCol = this._getDarkModeColor(elem, fontColor, false)
          if (newCol != null && newCol !== fontColor) {
            elem.setAttribute('color', newCol)
            this._foreColorMap[fontColor] = elem.getAttribute('color') ?? ''
            this._foreColorRevMap[newCol] = fontColor
            elem.setAttribute('data-nine-ac', fontColor)
          }
        }
      }
      if (
        elem.tagName === 'BODY' ||
        elem.tagName === 'MARQUEE' ||
        elem.tagName === 'TABLE' ||
        elem.tagName === 'THEAD' ||
        elem.tagName === 'TBODY' ||
        elem.tagName === 'TFOOT' ||
        elem.tagName === 'TR' ||
        elem.tagName === 'TH' ||
        elem.tagName === 'TD'
      ) {
        let bgColor = elem.getAttribute('bgcolor')
        if (elem === this.doc.body && win._bodyBgColor != null) {
          bgColor = win._bodyBgColor
        }
        if (backColor == null || backColor.length === 0) {
          if (
            bgColor != null &&
            bgColor.length > 0 &&
            (targetCol === this._getHexColor(bgColor) || elem.getAttribute('data-nine-ab') == null)
          ) {
            newCol = this._getDarkModeColor(elem, bgColor, true)
            if (newCol != null && newCol !== bgColor) {
              elem.removeAttribute('bgcolor')
              elem.style.backgroundColor = newCol
              this._backColorMap[bgColor] = elem.style.backgroundColor
              this._backColorRevMap[newCol] = bgColor
              elem.setAttribute('data-nine-ab', bgColor)
            }
          }
        } else if (
          bgColor != null &&
          bgColor.length > 0 &&
          (targetCol === this._getHexColor(bgColor) || elem.getAttribute('data-nine-ab') == null)
        ) {
          newCol = this._getDarkModeColor(elem, bgColor, true)
          if (newCol != null && newCol !== bgColor) {
            elem.setAttribute('bgcolor', newCol)
            this._foreColorMap[bgColor] = elem.getAttribute('bgcolor') ?? ''
            this._foreColorRevMap[newCol] = bgColor
            elem.setAttribute('data-nine-ab', bgColor)
          }
        }
      }

      const borderProps = ['borderTop', 'borderLeft', 'borderRight', 'borderBottom'] as const
      const borderAttrs = ['btc', 'blc', 'brc', 'bbc']
      const borderIsBack = false

      borderProps.forEach((item, idx) => {
        const styleRecord = elem.style as unknown as Record<string, string>
        const wProp = getCSSProp(styleRecord[item + 'Width'] ?? '')
        if (wProp.type === 1 && (wProp.number ?? 0) > 0) {
          const col = styleRecord[item + 'Color']
          if (
            col != null &&
            col.length > 0 &&
            (targetCol === this._getHexColor(col) ||
              elem.getAttribute('data-nine-' + borderAttrs[idx]) == null)
          ) {
            newCol = this._getDarkModeColor(elem, col, borderIsBack)
            if (newCol !== col) {
              styleRecord[item + 'Color'] = newCol
              this._foreColorMap[col] = styleRecord[item + 'Color']
              this._foreColorRevMap[newCol] = col
              elem.setAttribute('data-nine-' + borderAttrs[idx], col)
            }
          }
        }
      })
    }

    if (elem != null) {
      for (let i = 0; i < elem.children.length; i++) {
        this._checkElemColor(elem.children[i] as HTMLElement, targetCol)
      }
    }
  }

  _restoreElemColor(elem: HTMLElement): void {
    const sc = elem.getAttribute('data-nine-sc')
    const sb = elem.getAttribute('data-nine-sb')
    const ac = elem.getAttribute('data-nine-ac')
    const ab = elem.getAttribute('data-nine-ab')
    const btc = elem.getAttribute('data-nine-btc')
    const blc = elem.getAttribute('data-nine-blc')
    const brc = elem.getAttribute('data-nine-brc')
    const bbc = elem.getAttribute('data-nine-bbc')
    let cssChanged = false

    if (sc != null) {
      if (this._foreColorMap[sc] === elem.style.color) {
        elem.style.color = sc
        cssChanged = true
      }
      elem.removeAttribute('data-nine-sc')
    }
    if (sb != null) {
      if (this._backColorMap[sb] === elem.style.backgroundColor) {
        elem.style.backgroundColor = sb
        cssChanged = true
      }
      elem.removeAttribute('data-nine-sb')
    }
    if (ac != null) {
      if (this._foreColorMap[ac] === elem.getAttribute('color')) {
        elem.setAttribute('color', ac)
      }
      elem.removeAttribute('data-nine-ac')
    }
    if (ab != null) {
      const bgColor = elem.getAttribute('bgcolor')
      if (bgColor == null) {
        if (this._backColorMap[ab] === elem.style.backgroundColor) {
          elem.style.backgroundColor = ''
          elem.setAttribute('bgcolor', ab)
          cssChanged = true
        }
      } else if (this._backColorMap[ab] === bgColor) {
        elem.setAttribute('bgcolor', ab)
      }
      elem.removeAttribute('data-nine-ab')
    }
    const savedMap = this._backColorMap
    if (btc != null) {
      if (savedMap[btc] === elem.style.borderTopColor) {
        elem.style.borderTopColor = btc
        cssChanged = true
      }
      elem.removeAttribute('data-nine-btc')
    }
    if (blc != null) {
      if (savedMap[blc] === elem.style.borderLeftColor) {
        elem.style.borderLeftColor = blc
        cssChanged = true
      }
      elem.removeAttribute('data-nine-blc')
    }
    if (brc != null) {
      if (savedMap[brc] === elem.style.borderRightColor) {
        elem.style.borderRightColor = brc
        cssChanged = true
      }
      elem.removeAttribute('data-nine-brc')
    }
    if (bbc != null) {
      if (savedMap[bbc] === elem.style.borderBottomColor) {
        elem.style.borderBottomColor = bbc
        cssChanged = true
      }
      elem.removeAttribute('data-nine-bbc')
    }

    if (cssChanged === true) {
      const styleAttr = elem.getAttribute('style')
      if (styleAttr != null) elem.setAttribute('style', styleAttr)
    }

    for (let i = 0; i < elem.children.length; i++) {
      this._restoreElemColor(elem.children[i] as HTMLElement)
    }
  }

  _processSheet(sheet: CSSStyleSheet | CSSMediaRule | null): RuleListInfo {
    const ruleListInfo: RuleListInfo = { ruleInfoList: [] }
    if (sheet != null) {
      for (let i = 0; i < sheet.cssRules.length; i++) {
        const rule = sheet.cssRules[i]
        if (rule instanceof CSSMediaRule) {
          const mqRes = this._processSheet(rule)
          if (mqRes.ruleInfoList.length > 0) {
            ruleListInfo.ruleInfoList.push({
              queryCondition: rule.media.mediaText,
              ruleInfoList: mqRes.ruleInfoList,
              type: 4,
            })
          }
        } else if (rule instanceof CSSStyleRule) {
          const ruleInfo: CssRuleInfo = { selector: rule.selectorText, type: 1 }
          const color = rule.style.color
          if (color.length > 0 && color !== 'inherit' && color !== 'initial' && color !== 'unset') {
            ruleInfo.color = {
              value: color,
              isImportant: rule.style.getPropertyPriority('color') === 'important',
            }
          }
          const backCol = rule.style.getPropertyValue('background-color')
          if (
            backCol.length > 0 &&
            backCol !== 'inherit' &&
            backCol !== 'initial' &&
            backCol !== 'unset'
          ) {
            ruleInfo.backCol = {
              value: backCol,
              isImportant: rule.style.getPropertyPriority('background-color') === 'important',
            }
          }
          if (ruleInfo.color != null || ruleInfo.backCol != null) {
            ruleListInfo.ruleInfoList.push(ruleInfo)
          }
        }
      }
    }
    return ruleListInfo
  }

  private _getIndent(depth: number): string {
    let str = ''
    for (let i = 0; i < depth; i++) str += '  '
    return str
  }

  private _makeCssText(ruleInfo: CssRuleInfo, indent: number): string {
    let str = ''
    if (ruleInfo.type === 1) {
      str += this._getIndent(indent) + ruleInfo.selector + ' {\n'
      if (ruleInfo.color != null) {
        const color = this._getDarkModeColor(null, ruleInfo.color.value, false)
        str +=
          this._getIndent(indent + 1) +
          'color: ' +
          color +
          (ruleInfo.color.isImportant !== true ? '' : ' !important') +
          ';\n'
      }
      if (ruleInfo.backCol != null) {
        const backCol = this._getDarkModeColor(null, ruleInfo.backCol.value, true)
        str +=
          this._getIndent(indent + 1) +
          'background-color: ' +
          backCol +
          (ruleInfo.backCol.isImportant !== true ? '' : ' !important') +
          ';\n'
      }
      str += this._getIndent(indent) + '}\n'
    } else if (ruleInfo.type === 4) {
      const darkSchemeRegex = /prefers-color-scheme[ ]?:/i
      if (
        ruleInfo.queryCondition != null &&
        darkSchemeRegex.test(ruleInfo.queryCondition) !== true
      ) {
        str += this._getIndent(indent) + '@media ' + ruleInfo.queryCondition + ' {\n'
        const ruleText = this._getStyleText(ruleInfo.ruleInfoList ?? [], indent + 1)
        str += ruleText
        str += this._getIndent(indent) + '}\n'
      }
    }
    return str
  }

  private _getStyleText(ruleInfoList: CssRuleInfo[], indent: number): string {
    let str = ''
    for (let i = 0; i < ruleInfoList.length; i++) {
      str += this._makeCssText(ruleInfoList[i], indent)
    }
    return str
  }

  _checkStyleTagCss(target?: Document | null): void {
    this.darkModeInfo.foreLum = chroma(this.darkModeInfo.foreColor).luminance()
    this.darkModeInfo.backLum = chroma(this.darkModeInfo.backColor).luminance()

    const doc = target || document
    this._styleTagList = []

    const styleTagList = doc.querySelectorAll<HTMLStyleElement>(
      'style:not(#darkmode-info):not(#ios-css)',
    )
    for (let i = 0; i < styleTagList.length; i++) {
      const sheetInfo = this._processSheet(styleTagList[i].sheet)
      if (sheetInfo.ruleInfoList.length > 0) {
        const newStyleTag = document.createElement('style')
        newStyleTag.setAttribute('data-nine-style', 'true')
        const cssText = '\n' + this._getStyleText(sheetInfo.ruleInfoList, 1)
        if (cssText.trim().length > 0) {
          setHTML(newStyleTag, cssText)
          this._styleTagList.push({
            originTag: styleTagList[i],
            ruleInfoList: sheetInfo.ruleInfoList,
            darkModeTag: newStyleTag,
          })
        }
      }
    }

    for (let i = 0; i < this._styleTagList.length; i++) {
      const info = this._styleTagList[i]
      info.originTag.parentElement?.insertBefore(info.darkModeTag, info.originTag.nextSibling)
    }
  }

  _restoreStyleTagCss(target?: Document | HTMLElement | null): void {
    if (target != null) {
      const styleTagList = target.querySelectorAll('[data-nine-style=true]')
      for (let i = 0; i < styleTagList.length; i++) {
        styleTagList[i].parentElement?.removeChild(styleTagList[i])
      }
    } else {
      for (let i = 0; i < this._styleTagList.length; i++) {
        const info = this._styleTagList[i]
        info.darkModeTag.parentElement?.removeChild(info.darkModeTag)
      }
    }
  }

  async _imgToBase64(imgTag: HTMLImageElement): Promise<boolean> {
    const imgSrc =
      imgTag.src != null && imgTag.src.trim().length > 0 ? imgTag.src : imgTag.currentSrc
    if (imgSrc.indexOf('blob:') === 0) {
      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest()
        xhr.open('GET', imgSrc, true)
        xhr.responseType = 'blob'
        // blob: URL 은 메모리 참조라 ms 단위 완료 — 5 초면 충분 (Phase 2 §8.3 #1).
        xhr.timeout = 5_000
        xhr.onload = function () {
          if (this.status === 200) {
            const myBlob = this.response as Blob
            const reader = new FileReader()
            reader.onloadend = () => {
              const data = reader.result as string
              if (data.indexOf('data:') === 0) {
                imgTag.setAttribute('src', data)
                resolve(true)
              } else {
                console.log(' unknown image data type. ', data)
                resolve(false)
              }
            }
            reader.onerror = () => {
              console.log(' FileReader onerror ')
              resolve(false)
            }
            reader.readAsDataURL(myBlob)
          } else {
            console.log(' blob xhr non-200 status: ', this.status)
            resolve(false)
          }
        }
        xhr.onerror = () => {
          console.log(' blob onerror ')
          resolve(false)
        }
        xhr.ontimeout = () => {
          console.log(' blob ontimeout ')
          resolve(false)
        }
        xhr.send()
      })
    }
    return Promise.resolve(false)
  }

  getLinkColor(): { link: string; visited: string } {
    return this._linkColor
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// DocUtil — main document controller
// ─────────────────────────────────────────────────────────────────────────────

export class DocUtil {
  doc: Document
  nfCallback: ((param: string) => void) | null

  needRedefineCopy = true
  showRemoveZoomNodeBtn = true
  expandIconElem: HTMLElement | null = null

  mailViewMap: Record<string, MailView> = {}
  mailViewList: MailView[] = []

  colorMgr: EditorMgr
  parentType: string = 'desktop'

  private oldMetaTags: NodeListOf<HTMLMetaElement> | null = null
  private newMetaTag: HTMLElement | null = null

  private pre_css: HTMLElement | null = null
  private ios_css: HTMLElement | null = null
  private default_css: HTMLElement | null = null

  private _nineDarkMode = false
  darkModeApplied = false

  private copyEvtAdded = false
  private onCopy?: (e: ClipboardEvent) => Promise<void>
  private onCut?: (e: ClipboardEvent) => Promise<void>

  checkHeight?: (forced: boolean, isResize: boolean) => void

  get container(): HTMLElement {
    return this.mailViewList[0]?.container ?? this.doc.body
  }

  constructor(doc: Document, nfCallback: ((param: string) => void) | null) {
    this.doc = doc
    this.nfCallback = nfCallback

    if (this.showRemoveZoomNodeBtn === true) {
      const expandIconData = `<svg height="32" id="expand" viewBox="0 0 32 32" width="32" xmlns="http://www.w3.org/2000/svg"><path d=" M16 4 L28 4 L28 16 L24 12 L20 16 L16 12 L20 8z M4 16 L8 20 L12 16 L16 20 L12 24 L16 28 L4 28z "/></svg>`
      this.expandIconElem = document.createElement('div')
      this.expandIconElem.className = 'nf-removeZoomBtn'
      setHTML(this.expandIconElem, expandIconData)
    }

    // win.singleUse 초기화는 helper/singleUse.ts (createSingleUseFunc) 에서 lazy 하게 수행.

    this.colorMgr = new EditorMgr(this)

    if (win._tmpDarkMode != null) {
      this.setDarkForeColor(win._tmpDarkMode.foreColor)
      this.setDarkBackColor(win._tmpDarkMode.backColor)
      this.setDarkMode(true)
    }
  }

  callWebview(param: string): void {
    if (this.nfCallback != null) this.nfCallback(param)
  }

  setViewMetaTag(value: boolean): void {
    if (value === true) {
      this.oldMetaTags = this.doc.head.querySelectorAll<HTMLMetaElement>('meta[name=viewport]')
      this.newMetaTag = createTag(
        '<meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=yes"/>',
      )
      if (this.oldMetaTags.length > 0) {
        this.doc.head.insertBefore(this.newMetaTag, this.oldMetaTags[0])
        for (let i = 0; i < this.oldMetaTags.length; i++) {
          this.doc.head.removeChild(this.oldMetaTags[i])
        }
      } else {
        this.doc.head.appendChild(this.newMetaTag)
      }
    } else if (value === false) {
      if (this.oldMetaTags != null && this.oldMetaTags.length > 0 && this.newMetaTag != null) {
        for (let i = 0; i < this.oldMetaTags.length; i++) {
          this.doc.head.insertBefore(this.oldMetaTags[i], this.newMetaTag)
        }
      }
      if (this.newMetaTag != null) this.doc.head.removeChild(this.newMetaTag)
    }
    printLog(
      'screen width - ',
      window.innerWidth,
      window.screen.availWidth,
      document.documentElement.offsetWidth,
    )
  }

  setPreCSS(): void {
    const style = `
<style id="pre-css">
  table {
    color: inherit;
  }
</style>
`
    this.pre_css = createTag(style)
    const firstStyle = this.doc.head.querySelector('style')
    if (firstStyle != null) {
      this.doc.head.insertBefore(this.pre_css, firstStyle)
    } else {
      this.doc.head.appendChild(this.pre_css)
    }
  }

  setIOSCSS(): void {
    const style = `
<style id="ios-css">
  div {
    height: auto;
  }

  blockquote {
    margin-left: 0.8ex !important;
    margin-right: 0 !important;
    border-left:1px #ccc solid !important;
    padding-left: 1ex !important;
  }
</style>
`
    this.ios_css = createTag(style)
    this.doc.body.appendChild(this.ios_css)
  }

  setDefaultCSS(): void {
    const style = `
<style id="default-css">
  * {
    overflow-wrap: break-word;
  }
  .nf-removeZoomBtn {
    display: none;
    position: absolute;
    top: 0; left: 0;
    width: 32px; height: 32px;
    background-color: #fff;
    border: solid 1px #000;
    border-radius: 4px;
    opacity: 0.5;
  }
  .nf-zoom-container:hover > .nf-removeZoomBtn {
    display: block;
  }
</style>
`
    this.default_css = createTag(style)
    this.doc.head.appendChild(this.default_css)

    const isIOS = false
    if (isIOS) this.setIOSCSS()
  }

  addViewElement(key: string, element: HTMLElement): void {
    let mvObj: MailView
    if (this.mailViewMap[key] == null) {
      mvObj = new MailView(document, key, element, callWebView, this)
      this.mailViewMap[key] = mvObj
      this.mailViewList.push(mvObj)
    } else {
      mvObj = this.mailViewMap[key]
      if (mvObj.container === element) return
      mvObj.setContainer(element)
    }

    if (mvObj.container.style.visibility === 'hidden') {
      win._bodyBgColor = mvObj.container.getAttribute('bgcolor')
      mvObj.container.removeAttribute('bgcolor')
    }

    mvObj.onAdded()
  }

  removeViewElement(key: string): void {
    const mvObj = this.mailViewMap[key]
    if (mvObj != null) {
      mvObj.dispose()

      const idx = this.mailViewList.indexOf(mvObj)
      if (idx > -1) this.mailViewList.splice(idx, 1)
      delete this.mailViewMap[key]
    }
  }

  // 메일박스/스레드 전환 시 mailViewList 누적을 방지하기 위해 전체 일괄 정리.
  // dispose는 멱등(container == null이면 즉시 return)하므로 중복 호출에 안전.
  clearAllViewElements(): void {
    const list = this.mailViewList
    for (let i = 0; i < list.length; i++) {
      try {
        list[i].dispose()
      } catch (e) {
        console.warn('[DocUtil] MailView dispose 실패:', e)
      }
    }
    this.mailViewList = []
    this.mailViewMap = {}
  }

  notifyResized(): void {
    this.mailViewList.forEach((mvObj) => mvObj.notifyResized())
  }

  // ── 통합 zoom 재계산 스케줄러 (R7) ─────────────────────────────────────────
  // 메일뷰별 개별 setTimeout 대신 dirty set + 단일 타이머로 재계산 요청을 모아서 처리.
  // ZOOM_DEBOUNCE: 요청 후 대기, ZOOM_MIN_INTERVAL: 직전 실행과의 최소 간격(스로틀).
  private static readonly ZOOM_DEBOUNCE = 300
  private static readonly ZOOM_MIN_INTERVAL = 500
  private zoomDirtySet = new Set<MailView>()
  private zoomTimer: ReturnType<typeof setTimeout> | null = null
  // 높이 통지 전용 — 대기 중인 zoom 요청 타이머를 취소하지 않도록 분리.
  private zoomHeightTimer: ReturnType<typeof setTimeout> | null = null
  private zoomLastRunTime = 0

  requestZoom(mvObj: MailView): void {
    this.zoomDirtySet.add(mvObj)
    if (this.zoomTimer != null) clearTimeout(this.zoomTimer)
    const elapsed = Date.now() - this.zoomLastRunTime
    const delay = Math.max(DocUtil.ZOOM_DEBOUNCE, DocUtil.ZOOM_MIN_INTERVAL - elapsed)
    this.zoomTimer = setTimeout(() => this.runZoomScheduler(), delay)
  }

  private runZoomScheduler(): void {
    this.zoomTimer = null
    this.zoomLastRunTime = Date.now()
    const targets = Array.from(this.zoomDirtySet)
    this.zoomDirtySet.clear()
    let processed = 0
    targets.forEach((mvObj) => {
      // 접힌(높이 0)·해제된 메일뷰는 skip — 펼칠 때 notifyVisible 경로로 처리됨. (R3)
      if (mvObj.isZoomDisposed() === true || mvObj.container.offsetHeight === 0) return
      mvObj.runPartialZoom()
      processed += 1
    })
    if (processed > 0) this.notifyZoomHeight() // 실제 처리된 대상이 있을 때만 높이 통지.
  }

  // 높이만 변한 경우 — 재계산 없이 네이티브 높이 통지만 수행. (R6)
  notifyZoomHeightOnly(): void {
    if (this.zoomHeightTimer != null) clearTimeout(this.zoomHeightTimer)
    this.zoomHeightTimer = setTimeout(() => {
      this.zoomHeightTimer = null
      this.notifyZoomHeight()
    }, DocUtil.ZOOM_DEBOUNCE)
  }

  private notifyZoomHeight(): void {
    this.colorMgr.nativeCall('resizeHeight', null, [document.documentElement.scrollHeight])
  }

  cancelZoom(mvObj: MailView): void {
    this.zoomDirtySet.delete(mvObj)
  }

  // resetMailView → initMailView 재진입 시 이전 DocUtil의 pending 타이머가
  // stale 상태로 발화하지 않도록 정리. (orphan 타이머 방지)
  disposeZoomScheduler(): void {
    if (this.zoomTimer != null) {
      clearTimeout(this.zoomTimer)
      this.zoomTimer = null
    }
    if (this.zoomHeightTimer != null) {
      clearTimeout(this.zoomHeightTimer)
      this.zoomHeightTimer = null
    }
    this.zoomDirtySet.clear()
  }

  setBodyVisible(val: boolean): void {
    if (val === true) {
      if (win._bodyBgColor != null && this.isDarkMode() !== true) {
        document.body.setAttribute('bgcolor', win._bodyBgColor)
      }
      this.doc.body.style.visibility = ''
    } else {
      this.doc.body.style.visibility = 'hidden'
    }
  }

  checkEditableElem(): void {
    const editableElems = this.doc.querySelectorAll<HTMLElement>('[contenteditable]')
    for (let i = 0; i < editableElems.length; i++) {
      if (editableElems[i].getAttribute('contenteditable') === 'true') {
        editableElems[i].removeAttribute('contenteditable')
      }
    }
  }

  private onReadyMailView(): void {
    if (window.isReactReady === true && window.isMailViewReady === true) {
      this.colorMgr.nativeCall('onReady', (ret) => {
        console.log(' -- docUtil - onReady - ret : ', ret)
      })
    }
  }

  callOnReady(): void {
    this.parentType = 'desktop'
    if (window.parent != null && window.parent !== window) {
      const parentInitNative = (window.parent as ParentLikeWindow).initNative
      if (parentInitNative != null && resolveNineNative() == null) {
        const ret = parentInitNative()
        if (typeof ret === 'string') this.parentType = ret
      }
    }
    const nine = resolveNineNative()
    if (nine?.getNativeType != null) {
      this.parentType = nine.getNativeType()
    }

    setTimeout(() => {
      console.log(' -- docUtil - onReady : ')
      window.isMailViewReady = true
      this.onReadyMailView()
    }, 10)

    const processCopy = async (e: ClipboardEvent, _isCut: boolean): Promise<void> => {
      const target = e.target as HTMLElement | null
      if (target?.tagName.toLowerCase() === 'input') return

      const selection = document.getSelection()
      if (selection == null || e.clipboardData == null) return
      e.clipboardData.setData('text/plain', selection.toString())

      if (selection.rangeCount) {
        const rng = selection.getRangeAt(0)
        const cloneFragment = rng.cloneContents()
        const matched = cloneFragment.querySelector('.mail-message-content') as HTMLElement | null
        const realBody: HTMLElement | DocumentFragment = matched ?? cloneFragment

        const targetElem = document.createElement('body')
        while (realBody.firstChild != null) {
          targetElem.appendChild(realBody.firstChild)
        }

        if (this.mailViewList.length > 0) {
          this.mailViewList[0].removePartialZoom(targetElem, false, true)
        }

        let tmpNode: Node | null = rng.commonAncestorContainer
        while (
          tmpNode != null &&
          tmpNode.nodeType === 1 &&
          (tmpNode as HTMLElement).style.color.length === 0
        ) {
          tmpNode = tmpNode.parentNode
        }

        if (tmpNode != null && tmpNode.nodeType === 1) {
          const tmpEl = tmpNode as HTMLElement
          const nsc = tmpEl.getAttribute('data-nine-sc')
          const sc = tmpEl.style.color
          const children = targetElem.children
          for (let i = 0; i < children.length; i++) {
            const child = children[i] as HTMLElement
            if (child.style.color.length === 0) {
              child.style.color = sc
              if (nsc != null) child.setAttribute('data-nine-sc', nsc)
            }
          }
        }

        const imgs = targetElem.querySelectorAll('img')
        for (let i = 0; i < imgs.length; i++) {
          const imgSrc = imgs[i].src
          imgs[i].setAttribute('src', imgSrc)
          ;[
            'data-mce-id',
            'data-mce-object',
            'data-mce-src',
            'data-nine-resize',
            'data-nine-anichk',
            'data-nine-mimetype',
            'data-nine-dpi',
            'data-nine-src',
            'data-nine-size',
            'data-nine-flip-h',
            'data-nine-flip-v',
            'data-nine-rotate',
            'owa-cid',
            'data-cid',
          ].forEach((attr) => imgs[i].removeAttribute(attr))
          await this.colorMgr._imgToBase64(imgs[i])
        }

        const aTags = targetElem.querySelectorAll('a')
        for (let i = 0; i < aTags.length; i++) {
          const href = aTags[i].href
          aTags[i].setAttribute('href', href)
          aTags[i].setAttribute('data-mce-href', href)
        }

        if (this.isDarkMode() === true) {
          this.colorMgr._restoreElemColor(targetElem)
        }

        e.clipboardData.setData('text/html', targetElem.innerHTML)
      }

      e.preventDefault()
    }

    if (this.copyEvtAdded === true && this.onCopy != null && this.onCut != null) {
      document.removeEventListener('copy', this.onCopy)
      document.removeEventListener('cut', this.onCut)
    }

    this.onCopy = async (e: ClipboardEvent): Promise<void> => {
      console.log('copy - ', e)
      await processCopy(e, false)
    }
    this.onCut = async (e: ClipboardEvent): Promise<void> => {
      console.log('cut - ', e)
      await processCopy(e, true)
    }

    // §2c.4 + R23 — inner mode (window.parent === window) 에서는 host document 에
    // copy/cut listener 를 등록하지 않는다. iframe 전용 동작이며 inner mode 에서
    // host 의 복사/잘라내기를 가로채는 회귀를 회피한다.
    if (this.needRedefineCopy === true && isIframe) {
      this.copyEvtAdded = true
      document.addEventListener('copy', this.onCopy)
      document.addEventListener('cut', this.onCut)
    }
  }

  useOriginalCopy(value: boolean): void {
    this.needRedefineCopy = value === false
    if (this.needRedefineCopy && !this.copyEvtAdded && this.onCopy && this.onCut) {
      this.copyEvtAdded = true
      document.addEventListener('copy', this.onCopy)
      document.addEventListener('cut', this.onCut)
    } else if (!this.needRedefineCopy && this.copyEvtAdded && this.onCopy && this.onCut) {
      this.copyEvtAdded = false
      document.removeEventListener('copy', this.onCopy)
      document.removeEventListener('cut', this.onCut)
    }
  }

  setIOSVersion(version: string | number): void {
    const verStrs = String(version).split(/[._]/)
    const majorVersion = window.parseInt(verStrs[0], 10)
    console.log(' -- major version : ', majorVersion)
    if (majorVersion > 16) this.useOriginalCopy(true)
  }

  isDarkMode(): boolean {
    return this._nineDarkMode
  }

  setDarkMode(isDarkMode: boolean): void {
    if (this._nineDarkMode === isDarkMode) return
    this._nineDarkMode = isDarkMode

    const docElem = this.doc.body
    let savedValue: string | null = null

    if (isDarkMode === true) {
      if (docElem != null && docElem.style.backgroundColor.length > 0) {
        savedValue = docElem.getAttribute('data-nine-ac')
        if (savedValue == null) {
          docElem.setAttribute('data-nine-ac', docElem.style.backgroundColor)
          docElem.style.backgroundColor = 'transparent'
        }
      }
      this.darkModeApplied = true
      this.colorMgr.darkModeInfo.backLum = chroma(this.colorMgr.darkModeInfo.backColor).luminance()
      this.colorMgr.darkModeInfo.foreLum = chroma(this.colorMgr.darkModeInfo.foreColor).luminance()

      this.colorMgr._checkElemColor(this.doc.body)
      this.setDarkBackColor(this.colorMgr.darkModeInfo.backColor)
      this.setDarkForeColor(this.colorMgr.darkModeInfo.foreColor)

      this.colorMgr._checkStyleTagCss(this.doc)
    } else {
      if (docElem != null) {
        savedValue = docElem.getAttribute('data-nine-ac')
        if (savedValue != null) {
          docElem.style.backgroundColor = savedValue
          docElem.removeAttribute('data-nine-ac')
        }
      }
      this.colorMgr._restoreElemColor(this.doc.body)
      this.colorMgr._restoreStyleTagCss(this.doc)
      this.container.style.backgroundColor = ''
      this.removeDarkForeColor()
    }
  }

  applyDarkMode(): void {
    if (this.darkModeApplied === true && this._nineDarkMode === true) {
      this.colorMgr._restoreElemColor(this.doc.body)
      this.colorMgr._restoreStyleTagCss(this.doc)
      this.colorMgr._checkElemColor(this.doc.body)
      this.colorMgr._checkStyleTagCss(this.doc)
    }
  }

  getDarkBackColor(): string {
    return this.colorMgr.darkModeInfo.backColor
  }

  setDarkBackColor(color: string): void {
    this.colorMgr.darkModeInfo.backColor = color.toLowerCase()
    if (this.isDarkMode() === true) {
      this.container.style.backgroundColor = this.colorMgr.darkModeInfo.backColor
    }
  }

  getDarkForeColor(): string {
    return this.colorMgr.darkModeInfo.foreColor
  }

  setDarkForeColor(color: string): void {
    this.colorMgr.darkModeInfo.foreColor = color.toLowerCase()
    if (this.isDarkMode() === true) {
      const doc = document
      let styleTag = doc.querySelector<HTMLStyleElement>('#darkmode-info')
      if (styleTag == null) {
        styleTag = doc.createElement('style')
        styleTag.id = 'darkmode-info'
      }
      let cssDec = '\n  body { color: ' + this.colorMgr.darkModeInfo.foreColor + '; }'
      const linkColor = this.colorMgr._getDarkModeColor(
        null,
        this.colorMgr.getLinkColor().link,
        false,
      )
      const visitedColor = this.colorMgr._getDarkModeColor(
        null,
        this.colorMgr.getLinkColor().visited,
        false,
      )
      cssDec += '\n  a:link { color: ' + linkColor + '; }'
      cssDec += '\n  a:visited { color: ' + visitedColor + '; }'
      cssDec += '\n'
      setHTML(styleTag, cssDec)
      doc.head.appendChild(styleTag)
    }
  }

  private removeDarkForeColor(): void {
    const styleTag = this.doc.querySelector('#darkmode-info')
    if (styleTag != null) styleTag.parentElement?.removeChild(styleTag)
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// MailView — per-element view manager (zoom / smartview / resize)
// ─────────────────────────────────────────────────────────────────────────────

export class MailView {
  state = -1
  partialZoomMode = false
  smartViewMode = false
  zoomApplied = false

  options: MailViewOptions = {}
  nfCallback: ((param: string) => void) | null

  doc: Document
  docUtil: DocUtil
  key: string
  container: HTMLElement

  observerObj: ResizeObserverEntry | null = null
  isMediaQuery: boolean
  isHScroll = false
  optimizeEnabled = false
  isOnReadyCalled = false

  private originBodyInfo: BodyOriginInfo | null = null
  private bodyWidth = 0
  minZoom = 1
  private applyZoomTimer: number | null = null
  private disposed = false

  // 서킷 브레이커(R4) 상태 — runPartialZoom에서 사용.
  private zoomRunLog: number[] = []
  private zoomSuspendedUntil: number | null = null
  private lastZoomDelta: number | null = null
  private zoomResumeTimer: ReturnType<typeof setTimeout> | null = null

  constructor(
    doc: Document,
    key: string,
    container: HTMLElement | null,
    nfCallback: ((param: string) => void) | null,
    docUtil: DocUtil,
  ) {
    this.doc = doc
    this.docUtil = docUtil
    this.nfCallback = nfCallback
    this.key = key
    this.container = container || doc.body

    this.container.setAttribute('data-mailview', 'true')
    this.container.mvObj = this

    this.isMediaQuery = this.checkMediaQuery()
    this.attachResizeEvent()
  }

  get colorMgr(): EditorMgr {
    return this.docUtil.colorMgr
  }

  checkMediaQuery(): boolean {
    const styleElems = this.doc.querySelectorAll<HTMLStyleElement>('style')
    for (let i = 0; i < styleElems.length; i++) {
      const sheet = styleElems[i].sheet
      if (sheet == null) continue
      for (let j = 0; j < sheet.cssRules.length; j++) {
        if (sheet.cssRules[j] instanceof CSSMediaRule) return true
      }
    }
    return false
  }

  hasMediaQuery(): boolean {
    return this.isMediaQuery
  }

  checkHScroll(): boolean {
    return this.container.offsetWidth < this.container.scrollWidth
  }

  hasHScroll(): boolean {
    this.isHScroll = this.checkHScroll()
    if (!this.optimizeEnabled && this.isHScroll) {
      this.optimizeEnabled = true
      if (this.isOnReadyCalled === true) {
        console.log(' --- onOptimizeEnable')
        this.colorMgr.nativeCall('onOptimizeEnable', null, [])
      }
    }
    return this.isHScroll
  }

  setOptions(options: MailViewOptions): void {
    this.options = options
    if (this.options.mode === 'normal') this.setOptimized(false)
    else if (this.options.mode === 'optimized') this.setOptimized(true)
  }

  setBodyVisible(val: boolean): void {
    this.container.style.visibility = val === true ? '' : 'hidden'
  }

  showOptimizeBtn(val: boolean): void {
    if (win.webObj?.setOptimizedBtn != null) {
      win.webObj.setOptimizedBtn(this.key, val)
    }
  }

  hideAllImgs(val: boolean): void {
    const imgTags = this.container.querySelectorAll<HTMLImageElement>('img')
    for (let i = 0; i < imgTags.length; i++) {
      if (imgTags[i].initVisibility == null) {
        imgTags[i].initVisibility = imgTags[i].style.visibility
      }
      if (imgTags[i].initVisibility !== 'hidden') {
        if (val === true) {
          imgTags[i].style.visibility = 'hidden'
        } else {
          imgTags[i].style.visibility = imgTags[i].initVisibility || ''
        }
      }
    }
  }

  removePartialZoom(
    elem?: HTMLElement | null,
    removeImgEvent?: boolean,
    isCopy?: boolean,
    forced?: boolean,
  ): HTMLElement | null {
    const container: HTMLElement = elem || this.container

    if (elem == null) {
      const bInfo = this.originBodyInfo
      if (bInfo != null) {
        if (bInfo.width.length > 0) {
          bInfo.elem.style.setProperty('width', bInfo.width, bInfo.widthPriority)
        } else {
          bInfo.elem.style.removeProperty('width')
        }
        if (bInfo.minWidth.length > 0) {
          bInfo.elem.style.setProperty('min-width', bInfo.minWidth, bInfo.minWidthPriority)
        } else {
          bInfo.elem.style.removeProperty('min-width')
        }
      }
    }

    const removeZoomAttr = (node: HTMLElement): void => {
      const attr = node.getAttribute('data-nine-zoom-style')
      if (attr != null && attr.length > 0) {
        node.setAttribute('style', attr)
      } else {
        node.removeAttribute('style')
      }
      node.removeAttribute('data-nine-zoom-style')
      node.removeAttribute('data-nine-zoom-elem')
    }

    if (
      (container as Element) !== this.doc.documentElement &&
      container.getAttribute('data-nine-zoom-style') != null
    ) {
      removeZoomAttr(container)
    } else {
      const attrElems = container.querySelectorAll<HTMLElement>('[data-nine-zoom-style]')
      for (let i = 0; i < attrElems.length; i++) removeZoomAttr(attrElems[i])
    }

    let ret: HTMLElement | null = null

    const removeWrapper = (node: HTMLElement): void => {
      // 제거되는 컨테이너 하위 확대버튼의 click 리스너 해제 (미클릭 잔존 방지). (R20)
      const expandBtns = node.querySelectorAll<ExpandBtnElement>('.nf-removeZoomBtn')
      for (let b = 0; b < expandBtns.length; b++) {
        const handler = expandBtns[b].__nineExpandHandler
        if (handler != null) {
          expandBtns[b].removeEventListener('click', handler)
          expandBtns[b].__nineExpandHandler = undefined
        }
      }
      while (node.firstElementChild != null && node.firstElementChild.children.length > 0) {
        ret = node.firstElementChild.firstElementChild as HTMLElement
        node.parentElement?.insertBefore(ret, node)
      }
      node.parentElement?.removeChild(node)

      if (elem != null && forced === true && ret != null) {
        ret.setAttribute('data-nine-zoom-disabled', 'true')
      }
    }

    if (
      (container as Element) !== this.doc.documentElement &&
      container.classList.contains('nf-zoom-container') === true
    ) {
      removeWrapper(container)
    } else if (
      (container as Element) !== this.doc.documentElement &&
      container.getAttribute('data-nine-zoom-elem') != null
    ) {
      if (
        container.parentElement != null &&
        container.parentElement.classList.contains('nf-zoom-wrapper') === true
      ) {
        if (container.parentElement.parentElement != null) {
          removeWrapper(container.parentElement.parentElement)
        }
      } else {
        ret = container
      }
    } else {
      const elems = container.querySelectorAll<HTMLElement>('.nf-zoom-container')
      for (let i = 0; i < elems.length; i++) removeWrapper(elems[i])
      const wrapperElems = container.querySelectorAll<HTMLElement>('.nf-zoom-wrapper')
      for (let i = 0; i < wrapperElems.length; i++) {
        const node = wrapperElems[i]
        while (node.children.length > 0) {
          ret = node.firstElementChild as HTMLElement
          node.parentElement?.insertBefore(ret, node)
        }
        node.parentElement?.removeChild(node)
      }
    }

    if ((container as Element) === this.doc.documentElement || isCopy === true) {
      const tmpElems = container.querySelectorAll<HTMLElement>('[data-nine-zoom-elem]')
      for (let i = 0; i < tmpElems.length; i++) tmpElems[i].removeAttribute('data-nine-zoom-elem')
    }

    if (container != null) {
      const tmpElems = container.querySelectorAll<HTMLElement>('[data-nine-zoom-disabled]')
      for (let i = 0; i < tmpElems.length; i++)
        tmpElems[i].removeAttribute('data-nine-zoom-disabled')
    }

    if (removeImgEvent !== false) {
      const imgTags = this.container.querySelectorAll<HTMLImageElement>('img')
      for (let i = 0; i < imgTags.length; i++) {
        if (imgTags[i].imgListener != null) {
          imgTags[i].removeEventListener('load', imgTags[i].imgListener as EventListener)
          imgTags[i].removeEventListener('error', imgTags[i].imgListener as EventListener)
        }
      }
    }

    this.zoomApplied = false
    return ret
  }

  private applyPartialZoom(): void {
    const bodyElem = this.container

    const marginLeft = getCSSProp(window.getComputedStyle(bodyElem, null).marginLeft)
    const marginRight = getCSSProp(window.getComputedStyle(bodyElem, null).marginRight)

    let lMargin = 0
    let rMargin = 0
    if (marginLeft.type === 1) lMargin += marginLeft.number ?? 0
    if (marginRight.type === 1) rMargin += marginRight.number ?? 0

    // 재호출 시 직전 호출이 설정한 width(calc(100% - …))가 원본으로 저장되지 않도록 최초 1회만 저장.
    if (this.originBodyInfo == null) {
      this.originBodyInfo = {
        elem: bodyElem,
        width: bodyElem.style.width,
        widthPriority: bodyElem.style.getPropertyPriority('width'),
        minWidth: bodyElem.style.minWidth,
        minWidthPriority: bodyElem.style.getPropertyPriority('min-width'),
      }
    }

    bodyElem.style.setProperty('width', 'calc(100% - ' + (lMargin + rMargin) + 'px)', 'important')
    bodyElem.style.setProperty('min-width', 'unset', 'important')

    const bodyRect = bodyElem.getBoundingClientRect()
    this.bodyWidth = Math.round(bodyRect.width)

    const applyZoom = (targetElem: HTMLElement): void => {
      // L1 — DOMRect spread 는 own-enumerable 의존 (비표준 WebView 위험).
      // explicit destructure 로 cross-engine 안전성 확보.
      const rect = targetElem.getBoundingClientRect()
      if (rect.height === 0) return
      const oldRect = {
        left: rect.left - bodyRect.left,
        right: rect.right - bodyRect.left,
        x: rect.x - bodyRect.x,
        top: rect.top - bodyRect.top,
        bottom: rect.bottom - bodyRect.top,
        y: rect.y - bodyRect.y,
        width: rect.width,
        height: rect.height,
      }

      let availWidth = this.bodyWidth
      let curWidth = targetElem.offsetWidth

      let zoomInfo: ZoomInfo
      let container: HTMLElement
      let wrapper: HTMLElement
      let elem: HTMLElement
      let realWidth: number
      if (targetElem.getAttribute('data-nine-zoom-disabled') === 'true') return

      if (targetElem.classList.contains('nf-zoom-container') === false) {
        elem = targetElem
        if (
          elem.scrollWidth === 0 ||
          elem.scrollWidth < availWidth - 10 ||
          (elem.parentElement != null &&
            elem.parentElement.classList.contains('nf-zoom-wrapper') === true)
        ) {
          this.hideAllImgs(false)
          return
        }

        elem.setAttribute('data-nine-zoom-elem', 'true')
        zoomInfo = { originWidth: elem.scrollWidth, originHeight: elem.offsetHeight }

        container = createTag('<div class="nf-zoom-container"></div>')
        wrapper = createTag('<div class="nf-zoom-wrapper"></div>')
        container.zoomInfo = zoomInfo
        elem.zoomInfo = zoomInfo
        container.appendChild(wrapper)

        if (this.docUtil.expandIconElem != null) {
          container.style.position = 'relative'
          const expandBtnNode = this.docUtil.expandIconElem.cloneNode(true) as ExpandBtnElement
          container.appendChild(expandBtnNode)
          const onClickExpand = (e: MouseEvent): void => {
            this.removePartialZoom(container, false, false, true)
            e.stopPropagation()
            e.preventDefault()
            expandBtnNode.removeEventListener('click', onClickExpand)
            expandBtnNode.__nineExpandHandler = undefined
          }
          // 미클릭 상태로 dispose/removeWrapper될 때 리스너를 해제할 수 있도록 핸들러 보관.
          expandBtnNode.__nineExpandHandler = onClickExpand
          expandBtnNode.addEventListener('click', onClickExpand)
        }
        elem.parentElement?.insertBefore(container, elem)
        wrapper.appendChild(elem)

        let curTmp: HTMLElement | null = container.parentElement
        while (
          curTmp != null &&
          curTmp.parentElement !== bodyElem &&
          curTmp.parentElement != null &&
          window.getComputedStyle(curTmp, null).display === 'inline'
        ) {
          curTmp = curTmp.parentElement
        }
        if (curTmp != null && curTmp.offsetWidth > 0) curWidth = curTmp.offsetWidth
        if (curWidth > 0) availWidth = Math.min(curWidth, availWidth)

        realWidth = Math.max(oldRect.width, zoomInfo.originWidth)
      } else {
        zoomInfo = targetElem.zoomInfo as ZoomInfo
        container = targetElem
        wrapper = container.firstElementChild as HTMLElement
        elem = wrapper.firstElementChild as HTMLElement

        if (zoomInfo.originHeight !== elem.offsetHeight) zoomInfo.originHeight = elem.offsetHeight
        if (zoomInfo.originWidth !== elem.scrollWidth) zoomInfo.originWidth = elem.scrollWidth
        realWidth = zoomInfo.originWidth

        // 생성 분기와 동일한 부모 폭 clamp 적용 — 적용/해제 판단의 availWidth를 대칭화. (R1)
        let curTmp2: HTMLElement | null = container.parentElement
        while (
          curTmp2 != null &&
          curTmp2.parentElement !== bodyElem &&
          curTmp2.parentElement != null &&
          window.getComputedStyle(curTmp2, null).display === 'inline'
        ) {
          curTmp2 = curTmp2.parentElement
        }
        if (curTmp2 != null && curTmp2.offsetWidth > 0) curWidth = curTmp2.offsetWidth
        if (curWidth > 0) availWidth = Math.min(curWidth, availWidth)

        // 해제 판단은 2/3 하한 적용 전의 availWidth로 수행. 해제 마진(-30px)을
        // 생성 임계(-10px)보다 넓게 둬서 적용/해제가 동시에 성립하는 폭 구간을 제거. (R1 히스테리시스)
        if (realWidth <= availWidth - 30) {
          this.removePartialZoom(container, false, false)
          return
        }
      }

      if (availWidth < (this.bodyWidth * 2) / 3) availWidth = (this.bodyWidth * 2) / 3

      const ratio = Math.min(1, availWidth / realWidth)
      zoomInfo.ratio = ratio

      this.minZoom = Math.min(ratio, this.minZoom)

      wrapper.style.transform = 'scale(' + ratio + ', ' + ratio + ')'
      const dir = window.getComputedStyle(wrapper, null).direction
      wrapper.style.transformOrigin = dir === 'rtl' ? 'right top' : 'left top'

      container.style.width = availWidth + 'px'
      container.style.height = zoomInfo.originHeight * ratio + 'px'
      container.style.overflow = 'hidden'

      this.zoomApplied = true

      if (this.applyZoomTimer != null) cancelAnimationFrame(this.applyZoomTimer)

      const itemElem = elem
      const containerElem = container
      this.applyZoomTimer = requestAnimationFrame(() => {
        const containerRect = containerElem.getBoundingClientRect()
        const checkCSSPosition = (item: HTMLElement): void => {
          if (item.getAttribute('data-nine-zoom-style') != null) return
          const itemRect = item.getBoundingClientRect()
          if (itemRect.top < containerRect.top || itemRect.bottom > containerRect.bottom) {
            // hideAllImgs(true)가 깜박임 마스킹용으로 임시 부여한 visibility:hidden 이 스냅샷에 굳으면,
            // removeZoom(removeZoomAttr)에서 이 스냅샷을 style 로 복원할 때 이미지가 다시 숨겨진다.
            // transient 마스킹 속성이므로 스냅샷에서 제외한다(원본 zoom 레이아웃 스타일만 보존).
            const oldStyle = (item.getAttribute('style') || '')
              .replace(/(?:^|;)\s*visibility\s*:[^;]*/gi, '')
              .replace(/^\s*;+/, '')
              .trim()
            item.setAttribute('data-nine-zoom-style', oldStyle)
            item.style.position = 'static'
            item.style.margin = 'unset'
          } else {
            const elems = item.children
            for (let i = 0; i < elems.length; i++) {
              checkCSSPosition(elems[i] as HTMLElement)
            }
          }
        }
        checkCSSPosition(itemElem)
        this.hideAllImgs(false)
      })
    }

    const zoomCheck = (elem: HTMLElement): void => {
      if (elem.offsetParent != null && elem.children.length > 0) {
        const elems = elem.children
        for (let i = 0; i < elems.length; i++) {
          const childEl = elems[i] as HTMLElement
          if (childEl.classList.contains('nf-zoom-container') === false) {
            // inline 요소에는 zoom 컨테이너를 생성하지 않는다.
            const computedStyle = window.getComputedStyle(childEl, null)
            const isNotInline = computedStyle.display !== 'inline'
            if (childEl.scrollWidth === 0 || childEl.scrollWidth > this.bodyWidth) {
              if (childEl.offsetHeight > 0 && childEl.scrollWidth > this.bodyWidth) {
                const widthProp = getCSSProp(computedStyle.width)
                const minWidthProp = getCSSProp(computedStyle.minWidth)
                if (isNotInline && (widthProp.type === 1 || minWidthProp.type === 1)) {
                  if (childEl.offsetWidth > this.bodyWidth) {
                    applyZoom(childEl)
                    continue
                  }
                }
              }
              // L1 — DOMRect spread 회피 (applyZoom 의 oldRect 와 동일 패턴).
              const childRect = childEl.getBoundingClientRect()
              const bound = {
                left: childRect.left - bodyRect.left,
                right: childRect.right - bodyRect.left,
                x: childRect.x - bodyRect.x,
                top: childRect.top - bodyRect.top,
                bottom: childRect.bottom - bodyRect.top,
                y: childRect.y - bodyRect.y,
                width: childRect.width,
                height: childRect.height,
              }

              if (
                childEl.offsetHeight > 0 &&
                elem !== bodyElem &&
                bound.x > this.bodyWidth / 3 &&
                bound.right > this.bodyWidth &&
                bound.width > 0
              ) {
                applyZoom(elem)
              } else if (
                childEl.tagName === 'TABLE' ||
                (isNotInline &&
                  childEl.offsetHeight > 0 &&
                  childEl.scrollWidth > 0 &&
                  childEl.scrollWidth === childEl.offsetWidth)
              ) {
                applyZoom(childEl)
              } else {
                zoomCheck(childEl)
              }
            } else if (childEl.querySelector('.nf-zoom-container') != null) {
              zoomCheck(childEl)
            }
          } else {
            applyZoom(childEl)
          }
        }
      } else {
        applyZoom(elem)
      }
    }

    const imgTags = bodyElem.querySelectorAll<HTMLImageElement>('img')
    for (let i = 0; i < imgTags.length; i++) {
      const imgListener = (e: Event): void => {
        this.hasHScroll()
        const imgElem = e.target as HTMLImageElement
        const zoomContainer = imgElem.closest<HTMLElement>('.nf-zoom-container')
        if (zoomContainer != null) zoomContainer.style.height = ''
        this.notifyResized()
      }

      const imgElem = imgTags[i]
      // 이미지의 width / height 가 절대 단위 (px 등) 로 명시되어 있는지 검사.
      // 둘 다 명시된 경우 → 로드 후 크기 변화가 없으므로 load listener 부착 불필요.
      // 하나라도 명시 안 됐거나 상대 단위 (%, em 등) 이면 로드 후 크기가 바뀔 수 있어 listener 부착.
      // (§7.1#5 widthProp.num + §7.4#4 heightProp dead path 정정 — 사용자 의도 확정)
      const isFixedSize = (styleValue: string, attrName: 'width' | 'height'): boolean => {
        let prop = getCSSProp(styleValue)
        if (prop.type !== 1) {
          const attrValue = imgElem.getAttribute(attrName)
          if (attrValue != null && attrValue.trim().length > 0) {
            prop = getCSSProp(attrValue.trim(), true)
          }
        }
        return prop.type === 1 && prop.isRel === false
      }
      const hasWidth = isFixedSize(imgElem.style.width, 'width')
      const hasHeight = isFixedSize(imgElem.style.height, 'height')

      // 재계산마다 리스너가 중복 누적되지 않도록 등록 전 기존 리스너를 제거하고,
      // 크기가 고정된 이미지는 로드되어도 크기가 변하지 않으므로 리스너를 해제한다.
      if (!hasWidth || !hasHeight) {
        if (imgTags[i].imgListener != null) {
          imgTags[i].removeEventListener('load', imgTags[i].imgListener as EventListener)
        }
        imgTags[i].imgListener = imgListener
        imgTags[i].addEventListener('load', imgListener)
      } else if (imgTags[i].imgListener != null) {
        imgTags[i].removeEventListener('load', imgTags[i].imgListener as EventListener)
        imgTags[i].imgListener = undefined
      }
    }

    if (this.hasHScroll() === true || this.zoomApplied === true) {
      this.minZoom = 1
      zoomCheck(bodyElem)
      const zoomContainer = bodyElem.querySelector('.nf-zoom-container')
      if (zoomContainer == null) this.hideAllImgs(false)
      this.showOptimizeBtn(this.minZoom < 1)
    } else {
      this.showOptimizeBtn(false)
      this.hideAllImgs(false)
    }
  }

  onAdded(): void {
    setTimeout(() => {
      const hasHScroll = this.hasHScroll()
      // dev-emlview 하네스: 최적화(이미지 화면폭 축소)를 꺼 원본 크기로 렌더한다(raw 브라우저와 일치).
      const devNormal = win.__devDisableOptimize === true
      this.checkOption({ mode: devNormal ? 'normal' : 'optimized' })
      this.showOptimizeBtn(hasHScroll)
      if (!devNormal && win.NORMALIZE_MESSAGE_WIDTHS === true) {
        this.checkOption({ mode: 'optimized' })
      }
    }, 10)
  }

  notifyVisible(): void {
    const containers = this.container.querySelectorAll<HTMLElement>('.nf-zoom-container')
    containers.forEach((container) => {
      container.style.height = ''
    })
    // display none → visible 전환은 ResizeObserver에 기록되지 않으므로
    // prevWidth를 리셋해 becameVisible 가드가 확실히 동작하게 한다. (R6)
    if (this.observerObj != null) {
      this.observerObj.prevWidth = 0
    }
    this.notifyResized()
  }

  // 재계산 요청은 docUtil 레벨 통합 스케줄러로 모아서 처리한다. (R7)
  // 메일뷰별 개별 setTimeout(partialZoomTimer)은 제거됨.
  notifyResized(): void {
    if (this.partialZoomMode === true) {
      console.log(' -- notifyResized : ', Date.now())
      this.docUtil.requestZoom(this)
    } else {
      this.hideAllImgs(false)
    }
  }

  // 스케줄러가 호출하는 재계산 단일 진입점 — 서킷 브레이커(R4) + 재계산 + observer 동기화(R2).
  // 사용자 인터랙션/초기화 경로(setPartialZoom, setContainer)는 applyPartialZoom을
  // 직접 호출하므로 서킷 브레이커를 경유하지 않는다.
  runPartialZoom(): void {
    if (this.partialZoomMode !== true) return

    if (this.zoomSuspendedUntil != null) {
      if (Date.now() < this.zoomSuspendedUntil) return
      this.zoomSuspendedUntil = null
      this.zoomRunLog = []
    }

    const appliedBefore = this.zoomApplied
    const ratioBefore = this.minZoom

    console.log(' -- Resize~! : ', Date.now())
    this.applyPartialZoom()

    // 진동 신호 판정: 적용↔해제 전환은 항상 카운트, ratio 변화(>0.01)는 직전 변화와
    // 방향이 반전된 경우만 카운트. 단조 수렴(이미지 로드로 한 방향 이동)은 정상. (R4)
    let oscillating = appliedBefore !== this.zoomApplied
    const delta = this.minZoom - ratioBefore
    if (oscillating === false && Math.abs(delta) > 0.01) {
      oscillating = this.lastZoomDelta != null && delta > 0 !== this.lastZoomDelta > 0
      this.lastZoomDelta = delta
    }
    if (oscillating === true) {
      const now = Date.now()
      this.zoomRunLog = this.zoomRunLog.filter((t) => now - t < 10000)
      this.zoomRunLog.push(now)
      if (this.zoomRunLog.length >= 8) {
        console.warn('[mailview] partial zoom 진동 감지 - 30s 중단. key=', this.key)
        this.zoomSuspendedUntil = now + 30000
        // 중단 중 dirty 요청은 스케줄러 run()에서 소비·유실되므로 해제 시점의 재계산을 예약.
        if (this.zoomResumeTimer != null) clearTimeout(this.zoomResumeTimer)
        this.zoomResumeTimer = setTimeout(() => {
          this.zoomResumeTimer = null
          this.docUtil.requestZoom(this)
        }, 30000)
      }
    }

    // 재계산이 만든 크기 변화로 ResizeObserver가 재진입하지 않도록 observer 기준값을 동기화. (R2)
    // applyZoom 내부 rAF(checkCSSPosition) 이후 높이가 확정되므로 이중 rAF 뒤에 수행.
    // setContainer로 element가 교체된 경우엔 스킵 (이전 측정값을 새 element 기준에 오기록 방지).
    const syncTarget = this.observerObj != null ? this.observerObj.element : null
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (
          this.observerObj != null &&
          this.observerObj.element === syncTarget &&
          syncTarget != null
        ) {
          this.observerObj.prevWidth = syncTarget.offsetWidth
          this.observerObj.prevHeight = syncTarget.offsetHeight
        }
      })
    })
  }

  isZoomDisposed(): boolean {
    return this.disposed
  }

  setPartialZoom(value: boolean): void {
    if (value === true && this.smartViewMode === true) {
      throw new Error('smartView mode is already set.')
    }
    console.log(' - setPartialZoom : ', value)
    if (this.partialZoomMode === value) return

    this.partialZoomMode = value
    this.hideAllImgs(true)
    if (value === true) {
      this.applyPartialZoom()
    } else {
      this.removePartialZoom()
      this.hideAllImgs(false)
    }
  }

  private setActualView(value: boolean): void {
    const bodyElem = this.container
    if (value === true) {
      const imgTags = bodyElem.querySelectorAll<HTMLImageElement>('img')
      for (let i = 0; i < imgTags.length; i++) {
        const imgListenerAS = (_e: Event): void => {
          this.hasHScroll()
        }
        imgTags[i].imgListenerAS = imgListenerAS
        imgTags[i].addEventListener('load', imgListenerAS)
      }
    } else {
      const imgTags = bodyElem.querySelectorAll<HTMLImageElement>('img')
      for (let i = 0; i < imgTags.length; i++) {
        if (imgTags[i].imgListenerAS != null) {
          imgTags[i].removeEventListener('load', imgTags[i].imgListenerAS as EventListener)
        }
      }
    }
  }

  isOptimizeEnabled(): boolean {
    this.hasHScroll()
    return this.optimizeEnabled
  }

  setOptimized(value: boolean): void {
    this.setPartialZoom(value)
    this.setActualView(!value)
  }

  callWebview(param: string): void {
    if (this.nfCallback != null) this.nfCallback(param)
  }

  isModeEnabled(): boolean {
    const enabled = this.isOptimizeEnabled()
    setTimeout(() => {
      if (enabled === true) {
        this.callWebview('nine_mode_enabled')
      } else {
        this.callWebview('nine_mode_disabled')
      }
    }, 1)
    return enabled
  }

  setMode(mode: string): void {
    if (this.isOptimizeEnabled() !== true) {
      printLog('no need optimize.')
    } else if (mode === 'optimized') {
      this.setOptimized(true)
    } else {
      this.setOptimized(false)
    }
  }

  getMode(): 'none' | 'optimized' | 'normal' {
    if (this.isOptimizeEnabled() !== true) return 'none'
    if (this.partialZoomMode === true || this.smartViewMode) return 'optimized'
    return 'normal'
  }

  checkOption(opt?: MailViewOptions): void {
    const options: MailViewOptions = { uiBtn: true, mode: 'optimized' }
    if (opt?.mode != null) options.mode = opt.mode
    this.setOptions(options)
  }

  setBodyMargin(hMargin: string): void {
    this.container.style.setProperty('margin-left', hMargin, 'important')
    this.container.style.setProperty('margin-right', hMargin, 'important')
  }

  attachResizeEvent(): void {
    if (this.observerObj == null) {
      const observerEntry: ResizeObserverEntry = {
        element: this.container,
        prevWidth: -1,
        prevHeight: -1,
      }

      observerEntry.calcHeight = (): void => {
        if (observerEntry.resizeTimer != null) clearTimeout(observerEntry.resizeTimer)
        observerEntry.resizeTimer = setTimeout(() => {
          observerEntry.resizeTimer = null
          if (observerEntry.element != null) {
            const width = observerEntry.element.offsetWidth
            const height = observerEntry.element.offsetHeight
            const widthChanged = observerEntry.prevWidth !== width
            const becameVisible = observerEntry.prevWidth === 0 && width > 0
            // 재계산은 폭 변경/표시 전환 시에만. 높이만 변한 경우(콘텐츠 증가 등)는
            // 재계산 없이 네이티브 높이 통지만 수행한다. visible → hidden(폭 0)은 무시. (R6)
            if (width > 0 && (widthChanged || becameVisible)) {
              this.notifyResized()
            } else if (observerEntry.prevHeight !== height) {
              this.docUtil.notifyZoomHeightOnly()
            }
            observerEntry.prevWidth = width
            observerEntry.prevHeight = height
          }
        }, 300)
      }

      observerEntry.observer = new ResizeObserver(() => {
        observerEntry.calcHeight?.()
      })

      observerEntry.clear = (): void => {
        const { observer, resizeTimer } = observerEntry
        if (observer != null) {
          observer.disconnect()
          if (resizeTimer != null) clearTimeout(resizeTimer)
        }
      }

      observerEntry.start = (isFirstCall): void => {
        if (observerEntry.element != null) {
          if (isFirstCall === true) observerEntry.calcHeight?.()
          observerEntry.observer?.observe(observerEntry.element)
        }
      }

      this.observerObj = observerEntry
    } else {
      this.observerObj.clear?.()
      this.observerObj.element = this.container
      this.observerObj.prevWidth = -1
      this.observerObj.prevHeight = -1
    }

    if (this.doc.readyState === 'complete' || this.doc.readyState === 'interactive') {
      this.observerObj.start?.(true)
    } else {
      this.doc.addEventListener('DOMContentLoaded', () => {
        this.observerObj?.start?.(true)
      })
    }
  }

  setContainer(element: HTMLElement): void {
    this.observerObj?.clear?.()
    this.container = element
    if (this.observerObj != null) {
      this.observerObj.element = this.container
      // 이전 container의 기준값은 새 container에 적용하지 않음.
      this.observerObj.prevWidth = -1
      this.observerObj.prevHeight = -1
    }
    // 이전 container의 width 원본 정보는 새 container에 적용하지 않음.
    this.originBodyInfo = null
    if (this.partialZoomMode === true) this.applyPartialZoom()
    this.observerObj?.start?.()
  }

  // removeViewElement에서 호출. observer/타이머/이미지 리스너를 정리하여 누수를 방지.
  dispose(): void {
    // 멱등성 가드 — 이미 dispose된 경우 즉시 return.
    // (removeViewElement → clearAllViewElements 중복 경로에서 재정리 throw 방지)
    if (this.disposed === true) {
      return
    }
    this.disposed = true
    this.observerObj?.clear?.()
    this.docUtil.cancelZoom(this)
    if (this.zoomResumeTimer != null) {
      clearTimeout(this.zoomResumeTimer)
      this.zoomResumeTimer = null
    }
    if (this.applyZoomTimer != null) {
      cancelAnimationFrame(this.applyZoomTimer)
      this.applyZoomTimer = null
    }
    const imgTags = this.container.querySelectorAll<HTMLImageElement>('img')
    for (let i = 0; i < imgTags.length; i++) {
      if (imgTags[i].imgListener != null) {
        imgTags[i].removeEventListener('load', imgTags[i].imgListener as EventListener)
        imgTags[i].imgListener = undefined
      }
      if (imgTags[i].imgListenerAS != null) {
        imgTags[i].removeEventListener('load', imgTags[i].imgListenerAS as EventListener)
        imgTags[i].imgListenerAS = undefined
      }
    }
    // 미클릭 상태로 남은 확대버튼 click 리스너 해제. (R20)
    const expandBtns = this.container.querySelectorAll<ExpandBtnElement>('.nf-removeZoomBtn')
    for (let e = 0; e < expandBtns.length; e++) {
      const handler = expandBtns[e].__nineExpandHandler
      if (handler != null) {
        expandBtns[e].removeEventListener('click', handler)
        expandBtns[e].__nineExpandHandler = undefined
      }
    }
    this.container.mvObj = undefined
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile WebView legacy globals (uiWebview_*, isOfficeMailApp, etc.)
// ─────────────────────────────────────────────────────────────────────────────

interface UIWebViewState {
  resultCount: number
  foreColor: string
  backColor: string
}

const uiWebviewState: UIWebViewState = { resultCount: 0, foreColor: 'black', backColor: 'yellow' }

function uiWebview_HighlightAllOccurencesOfStringForElement(
  element: Node | null,
  keyword: string,
): void {
  if (element == null) return
  if (element.nodeType === 3) {
    let count = 0
    let elementTmp: Text = element as Text
    while (true) {
      const value = elementTmp.nodeValue ?? ''
      const idx = value.toLowerCase().indexOf(keyword)
      if (idx < 0) break
      count++
      elementTmp = document.createTextNode(value.substring(idx + keyword.length))
    }
    uiWebviewState.resultCount += count
    let index = uiWebviewState.resultCount
    let cursor: Text = element as Text
    while (true) {
      const value = cursor.nodeValue ?? ''
      const idx = value.toLowerCase().indexOf(keyword)
      if (idx < 0) break
      const span = document.createElement('span')
      const text = document.createTextNode(value.substring(idx, idx + keyword.length))
      span.appendChild(text)
      span.setAttribute('class', 'uiWebviewHighlight')
      span.style.backgroundColor = uiWebviewState.backColor
      span.style.color = uiWebviewState.foreColor
      index--
      span.setAttribute('id', 'SEARCH WORD' + index)
      const newText = document.createTextNode(value.substring(idx + keyword.length))
      cursor.deleteData(idx, value.length - idx)
      const next = cursor.nextSibling
      cursor.parentNode?.insertBefore(span, next)
      cursor.parentNode?.insertBefore(newText, next)
      cursor = newText
    }
  } else if (element.nodeType === 1) {
    const elemNode = element as HTMLElement
    if (elemNode.style.display !== 'none' && elemNode.nodeName.toLowerCase() !== 'select') {
      for (let i = elemNode.childNodes.length - 1; i >= 0; i--) {
        uiWebview_HighlightAllOccurencesOfStringForElement(elemNode.childNodes[i], keyword)
      }
    }
  }
}

function uiWebview_RemoveAllHighlightsForElement(element: Node | null): boolean {
  if (element == null) return false
  if (element.nodeType === 1) {
    const elem = element as HTMLElement
    if (elem.getAttribute('class') === 'uiWebviewHighlight') {
      const text = elem.removeChild(elem.firstChild as Node)
      elem.parentNode?.insertBefore(text, elem)
      elem.parentNode?.removeChild(elem)
      return true
    }
    let normalize = false
    for (let i = elem.childNodes.length - 1; i >= 0; i--) {
      if (uiWebview_RemoveAllHighlightsForElement(elem.childNodes[i])) normalize = true
    }
    if (normalize) elem.normalize()
  }
  return false
}

function uiWebview_RemoveAllHighlights(): void {
  uiWebviewState.resultCount = 0
  uiWebview_RemoveAllHighlightsForElement(document.body)
}

function uiWebview_HighlightAllOccurencesOfString(
  keyword: string,
  fColor: string | null,
  bColor: string | null,
): void {
  if (fColor != null) uiWebviewState.foreColor = fColor
  if (bColor != null) uiWebviewState.backColor = bColor
  uiWebview_RemoveAllHighlights()
  if (keyword == null || String(keyword).length === 0) {
    // 빈 검색어는 indexOf가 항상 0을 반환하여 무한루프가 발생함.
    return
  }
  uiWebview_HighlightAllOccurencesOfStringForElement(document.body, keyword.toLowerCase())
}

function uiWebview_ScrollTo(idx: number): void {
  const scrollTo = document.getElementById('SEARCH WORD' + idx)
  scrollTo?.scrollIntoView()
}

// iframe 컨텍스트에서만 글로벌 등록 — non-iframe 에서는 named export 로 직접 import 사용.
if (isIframe) {
  ;(globalThis as Record<string, unknown>).uiWebview_HighlightAllOccurencesOfString =
    uiWebview_HighlightAllOccurencesOfString
  ;(globalThis as Record<string, unknown>).uiWebview_RemoveAllHighlights =
    uiWebview_RemoveAllHighlights
  ;(globalThis as Record<string, unknown>).uiWebview_ScrollTo = uiWebview_ScrollTo
}

// ─────────────────────────────────────────────────────────────────────────────
// Bootstrap (named export — invoked by ViewerIFace)
// ─────────────────────────────────────────────────────────────────────────────

let mailViewInitialized = false

// inner mode 의 resetMailView() -> initMailView() 재진입 시 startMailView 의 지역 변수가
// 새로 생성되어 resize/load listener 가 host window 에 중복 등록되는 회귀 (viewer-refactor
// -task.md A5 / critic iter1 C-1) 회피용. 매 startMailView 호출 시 이전 handler 제거 후
// 새 handler 등록. handler closure 가 새 docUtil / checkHeight 를 참조하도록 함.
let currentResizeHandler: ((e: Event) => void) | null = null
let currentLoadHandler: (() => void) | null = null

export function _swapResizeListener(newHandler: (e: Event) => void): void {
  if (currentResizeHandler != null) {
    window.removeEventListener('resize', currentResizeHandler)
  }
  currentResizeHandler = newHandler
  window.addEventListener('resize', currentResizeHandler)
}

export function _swapLoadListener(newHandler: () => void): void {
  if (currentLoadHandler != null) {
    window.removeEventListener('load', currentLoadHandler)
  }
  currentLoadHandler = newHandler
  window.addEventListener('load', currentLoadHandler)
}

export function _resetListenerRefsForTest(): void {
  currentResizeHandler = null
  currentLoadHandler = null
}

function startMailView(isLoadedState: boolean): void {
  if (document.body.style.visibility === 'hidden') {
    win._bodyBgColor = document.body.getAttribute('bgcolor')
    document.body.removeAttribute('bgcolor')
  }

  const docUtil = new DocUtil(document, callWebView)
  docUtil.setViewMetaTag(true)
  docUtil.setDefaultCSS()
  docUtil.checkEditableElem()

  window.docUtil = docUtil

  let lastHeight = 0
  let prevHeight = 0
  let timerH: number | null = null

  const notifyResize = (): void => {
    const sizeNotification = win.webkit?.messageHandlers?.sizeNotification
    if (sizeNotification != null) {
      sizeNotification.postMessage({
        width: document.body.scrollWidth,
        height: document.body.scrollHeight,
      })
    }
  }

  const enableResizeListener = (): void => {
    _swapResizeListener((e) => {
      printLog('resized - ', e)
      checkHeight(false, true)
    })
  }

  const checkHeight = (forced: boolean, isResize: boolean): void => {
    if (timerH != null) cancelAnimationFrame(timerH)
    timerH = requestAnimationFrame(() => {
      timerH = null
      const newHeight = document.documentElement.scrollHeight
      console.log(' -- checkHeight : ', newHeight, prevHeight, isResize)
      if (isResize === true) {
        // 창 리사이즈로 인한 재계산은 각 mailView container의 ResizeObserver가
        // 폭 변화를 감지해 처리하므로(R6/R8), 여기서는 전체 fan-out 없이
        // 네이티브 높이 통지만 수행한다.
        docUtil.colorMgr.nativeCall('resizeHeight', null, [newHeight])
        prevHeight = newHeight
      }
      printLog('check height - ' + newHeight)
      if (lastHeight === newHeight) {
        docUtil.setBodyVisible(true)
        if (forced === true) {
          printLog('new rendering')
          document.body.style.display = 'none'
          requestAnimationFrame(() => {
            document.body.style.display = ''
          })
        }
        docUtil.callWebview('nine_rendering_finished')
        notifyResize()
        enableResizeListener()
      } else {
        lastHeight = newHeight
        checkHeight(false, false)
      }
    })
  }

  docUtil.checkHeight = checkHeight

  if (isLoadedState === true) {
    lastHeight = document.documentElement.scrollHeight
    printLog('lastHeight - ' + lastHeight)
    docUtil.callOnReady()
    checkHeight(false, false)
  } else {
    // timeout이 먼저 실행된 뒤 load 이벤트가 도착하면 callOnReady가 중복 호출되므로 1회만 실행되도록 보장.
    let onReadyFired = false
    const fireOnReady = (forced: boolean): void => {
      if (onReadyFired === true) return
      onReadyFired = true
      lastHeight = document.documentElement.scrollHeight
      printLog('lastHeight - ' + lastHeight)
      docUtil.callOnReady()
      checkHeight(forced, false)
    }

    const timerForLoading = setTimeout(() => {
      printLog('window load - timeout')
      fireOnReady(true)
    }, 1000)

    _swapLoadListener(() => {
      clearTimeout(timerForLoading)
      printLog('window load')
      fireOnReady(false)
    })
  }

  // §2c.4 + R22 — calendar event click 은 더 이상 document-level listener 가 아닌
  // EmailView 의 mailBody onClick (handleSchemaEventClick) 으로 처리한다.
  // iframe / inner mode 모두 React handler 로 일원화 — host document 에 등록되어
  // viewer 외 host UI 클릭까지 catch 하던 회귀 (R22) 가 구조적으로 차단된다.
}

/**
 * schema.org/Event 메타데이터를 가진 viewer DOM 클릭을 감지하여
 * docUtil.colorMgr.notifyCreateInvitation 으로 native 초대 생성 흐름을 trigger.
 *
 * iframe / inner mode 양쪽에서 EmailView 의 mailBody onClick 으로 호출되며,
 * mailviewForThread.js 시절 document-level click listener 의 자리를 대체한다.
 */
export function handleSchemaEventClick(target: EventTarget | null): void {
  if (!(target instanceof Element)) return

  let tbody: HTMLElement | null = null
  let item: HTMLElement | null = null
  let cursor: HTMLElement | null = target as HTMLElement
  do {
    tbody = cursor?.closest('tbody') ?? null
    if (tbody != null) {
      item = tbody.querySelector<HTMLElement>('[itemscope][itemprop="option"]')
      cursor = tbody.parentElement
    }
  } while (cursor != null && tbody != null && item == null)

  if (item != null && tbody === item.parentElement) {
    const itemType = item.getAttribute('itemtype')
    if (itemType === 'http://schema.org/Event') {
      const displayStr = readDisplayText(item)
      const startElem = item.querySelector('time[itemprop="startDate"]')
      const endElem = item.querySelector('time[itemprop="endDate"]')
      if (startElem != null && endElem != null) {
        const startDate = startElem.getAttribute('datetime')
        const endDate = endElem.getAttribute('datetime')
        if (startDate != null && endDate != null) {
          window.docUtil?.colorMgr.notifyCreateInvitation(startDate, endDate, displayStr)
          return
        }
      }
    }
  }

  const formTbody = (target as HTMLElement).closest<HTMLElement>(
    'input[value="itemtype-event"] + form',
  )
  if (formTbody != null) {
    const startElem = formTbody.querySelector('input[type="hidden"]:nth-of-type(1)')
    const endElem = formTbody.querySelector('input[type="hidden"]:nth-of-type(2)')
    if (startElem != null && endElem != null) {
      const displayStr = readDisplayText(formTbody)
      const startDate = startElem.getAttribute('value')
      const endDate = endElem.getAttribute('value')
      if (startDate != null && endDate != null) {
        window.docUtil?.colorMgr.notifyCreateInvitation(startDate, endDate, displayStr)
      }
    }
  }
}

// innerText 가 미정의인 환경 (jsdom 28 등) 에서도 안전하게 동작하도록 fallback.
function readDisplayText(elem: HTMLElement): string {
  return (elem.innerText ?? elem.textContent ?? '').trim()
}

/**
 * 명시적 초기화 진입점. ViewerIFace.initIFace 가 적절한 시점에 호출한다.
 * 중복 호출은 안전하게 무시된다.
 */
export function initMailView(): void {
  if (mailViewInitialized) return
  mailViewInitialized = true

  printLog('first readyState - ' + document.readyState)

  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    printLog('readyState - complete')
    startMailView(document.readyState === 'complete')
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      printLog('DOMContentLoaded.')
      startMailView(false)
    })
  }
}

/**
 * InnerViewer 의 reload 패턴 (EmailContainer 강제 remount) 시 호출.
 *
 * `mailViewInitialized` once-guard 를 reset 하여 다음 `initMailView()` 호출 시
 * `startMailView` 가 재실행되도록 한다. iframe 모드는 `location.reload()` 가
 * JS context 자체를 reset 하므로 본 함수 불필요. inner mode 의 reload 동작 회귀
 * (stale DocUtil 이 destroyed DOM 의 bodyElem 참조) 회피용.
 *
 * 메모리 누수 측면 — 이전 DocUtil instance 의 event listener cleanup 은 별도
 * follow-up (docutil-resolver-task.md §11). 현재는 가드 reset 으로 회귀만 차단.
 */
export function resetMailView(): void {
  // 재진입 시 새 DocUtil이 생성되므로, 이전 인스턴스의 zoom 스케줄러 pending
  // 타이머가 stale 상태(분리된 MailView)로 발화하지 않도록 정리한다.
  window.docUtil?.disposeZoomScheduler()
  mailViewInitialized = false
}

// ─────────────────────────────────────────────────────────────────────────────
// Mobile WebView legacy API — function declarations + iframe-only global registration
// ─────────────────────────────────────────────────────────────────────────────
//
// 다음 함수들은 모듈 내부 호출 / named export / iframe 글로벌 등록 세 경로 모두에서
// 일관되게 동작하도록 top-level function declaration 으로 정의. iframe 컨텍스트에서만
// `window` / `globalThis` 에 등록된다 (§8.2.2 글로벌 노출의 조건부 선언).

function isOfficeMailApp(): boolean {
  return window.navigator.userAgent.indexOf('OfficeMail') >= 0
}

function nineLog(msg: string): void {
  location.href = 'nine_log_?' + msg
}

function img_find(): string[] {
  const imgs = document.getElementsByTagName('img')
  const imgSrcs: string[] = []
  for (let i = 0; i < imgs.length; i++) imgSrcs.push(imgs[i].src)
  return imgSrcs
}

function reloadInlineImage(cid: string, uri: string): void {
  const images = document.getElementsByTagName('img')
  for (let i = 0; i < images.length; i++) {
    const image = images[i]
    if (image == null || image.src == null) continue
    const orgSrc = image.src
    if (orgSrc.indexOf(cid) === 0) image.src = uri
  }
}

// 글로벌 진입점 setDarkMode — host / native bridge 가 iframe.contentWindow.setDarkMode(...)
// 으로 호출. 내부 식별자가 DocUtil.setDarkMode 와 충돌하지 않도록 별도 이름.
function setGlobalDarkMode(backColor: string, foreColor: string): void {
  if (window.docUtil == null) {
    win._tmpDarkMode = { backColor, foreColor }
  } else {
    window.docUtil.setDarkForeColor(foreColor)
    window.docUtil.setDarkBackColor(backColor)
    window.docUtil.applyDarkMode()
    window.docUtil.setDarkMode(true)
  }
}

// iframe 컨텍스트에서만 글로벌 등록 — non-iframe 에서는 named export 로 직접 import 사용.
if (isIframe) {
  if (win.MailView == null) {
    win.MailView = MailView
  }
  win.isOfficeMailApp = isOfficeMailApp
  win.nineLog = nineLog
  win.img_find = img_find
  win.reloadInlineImage = reloadInlineImage
  win.setDarkMode = setGlobalDarkMode
}

// non-iframe consumer 를 위한 named exports.
export {
  isOfficeMailApp,
  nineLog,
  img_find,
  reloadInlineImage,
  setGlobalDarkMode,
  uiWebview_HighlightAllOccurencesOfString,
  uiWebview_RemoveAllHighlights,
  uiWebview_ScrollTo,
}

export type { NineNativeAPI }
